import { useCallback, useEffect, useRef, useState } from 'react';

export function clampWidth(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

interface UseResizablePanelOptions {
  /** localStorage key the chosen width is persisted under. */
  storageKey: string;
  min: number;
  max: number;
  /** CSS default width, used as the drag start point before the first resize. */
  defaultWidth: number;
  /**
   * Which edge the drag handle lives on. A left-anchored panel (the inspector)
   * has its handle on the RIGHT edge → dragging right widens it. A right-anchored
   * panel (the AI assistant) has its handle on the LEFT edge → dragging left widens.
   */
  edge: 'left' | 'right';
}

/**
 * A7 — persistent, draggable panel width. Returns `width` (null = fall back to
 * the CSS default until the user resizes once) and a pointer-down handler for a
 * resize handle. Width is clamped to [min,max] and saved to localStorage so it
 * survives reloads. Uses pointer events + a body cursor/no-select lock during
 * the drag so the canvas underneath doesn't hijack it.
 */
export function useResizablePanel({ storageKey, min, max, edge, defaultWidth }: UseResizablePanelOptions) {
  const [width, setWidth] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      const n = raw ? parseInt(raw, 10) : NaN;
      return Number.isFinite(n) ? clampWidth(n, min, max) : null;
    } catch {
      return null;
    }
  });

  const drag = useRef<{ startX: number; startW: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    if (width != null) {
      try {
        localStorage.setItem(storageKey, String(width));
      } catch {
        /* ignore quota / disabled storage */
      }
    }
  }, [width, storageKey]);

  const onHandlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      e.preventDefault();
      const startW = width ?? defaultWidth;
      drag.current = { startX: e.clientX, startW };
      setIsResizing(true);

      const prevCursor = document.body.style.cursor;
      const prevSelect = document.body.style.userSelect;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const onMove = (ev: PointerEvent) => {
        if (!drag.current) return;
        const dx = ev.clientX - drag.current.startX;
        const delta = edge === 'right' ? dx : -dx;
        setWidth(clampWidth(drag.current.startW + delta, min, max));
      };
      const onUp = () => {
        drag.current = null;
        setIsResizing(false);
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevSelect;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [width, edge, min, max, defaultWidth],
  );

  return { width, onHandlePointerDown, isResizing };
}

// Made with Bob
